import React from 'react'
import { SparklesIcon } from '@heroicons/react/24/outline'
import { AcademicCap } from '@components/common/icons/custom-icons'
import {
  AlternatingSection,
  AlternatingSectionProps,
} from '@components/about-us/curriculum'

const SECTIONS: (AlternatingSectionProps & { reverse: boolean })[] = [
  {
    badge: {
      icon: <AcademicCap className="h-4 w-4" />,
      text: 'Support for growth',
      className: 'text-orange bg-orange-200 border border-orange-300',
    },
    items: [
      {
        heading: 'Ongoing training',
        description:
          'Continuous upskilling to improve teaching techniques and classroom delivery.',
      },
      {
        heading: 'Mentorship and regular feedback',
        description:
          'Guidance from experienced mentors, with feedback that helps improve week after week.',
      },
      {
        heading: 'Teaching resources & classroom support',
        description:
          'Guidance, playbooks, and examples help educators teach effectively in the Codingal format from Day 1.',
      },
    ],
    imageSrc: '/images/teacher-lp/community/support-for-growth.png',
    imageAlt: 'Teachers in online sessions',
    imageWidth: 601,
    imageHeight: 369,
    reverse: true,
  },
  {
    badge: {
      icon: <SparklesIcon className="h-4 w-4" />,
      text: 'Interactive platforms and in-house curriculum',
      className:
        'text-violet-100 bg-violet-50 border border-violet-100 border-opacity-25',
    },
    items: [
      {
        heading: 'Built in house with purpose',
        description:
          'A teaching platform designed and improved continuously, aligned with how children learn and how educators teach.',
      },
      {
        heading: 'Flexible teaching platforms',
        description:
          'A classroom setup powered by partners like Pencil Spaces and Whereby, with the ability to switch video platforms mid-class without disruption.',
      },
      {
        heading: 'Expert curriculum with STEM.org accreditation',
        description:
          'In-house curriculum built by subject experts for K–12 progression, with STEM.org accredited courses that add credibility to learner outcomes.',
      },
    ],
    imageSrc: '/images/teacher-lp/community/interactive-platforms.png',
    imageAlt: 'Interactive teaching platforms',
    imageWidth: 655,
    imageHeight: 589,
    reverse: false,
  },
  {
    badge: {
      icon: <SparklesIcon className="h-4 w-4" />,
      text: 'Personalized dashboard for educators',
      className:
        'text-blue-300 bg-blue-50 border border-blue-300 border-opacity-25',
    },
    items: [
      {
        heading: 'Calendar and schedule clarity',
        description:
          'Week and month views to plan teaching slots, track upcoming classes, and manage availability with confidence.',
      },
      {
        heading: 'My students and project reviews',
        description:
          'A clear view of your students with a structured project review queue, due dates, feedback summaries, and one-click review actions.',
      },
      {
        heading: 'Transparent earnings and teacher support',
        description:
          'Live earning visibility with easy access to payout details, backed by fast support and an active teacher community.',
      },
    ],
    imageSrc: '/images/teacher-lp/community/personalized-dashboard.png',
    imageAlt: 'Educator dashboard with calendar and student views',
    imageWidth: 612,
    imageHeight: 380,
    reverse: true,
  },
]

const LearningCommunity: React.FC = () => (
  <div className="px-5 py-12 lg:py-16 bg-white">
    <div className="container mx-auto">
      <div className="text-center mb-2 lg:mb-4 max-w-4xl mx-auto">
        <h2 className="pb-3 text-2xl text-center text-grey lg:text-4xl font-600">
          A learning community built with care
        </h2>
        <p className="text-lg text-grey mx-auto leading-tight">
          From how educators get trained to how curriculum and tools get
          designed, every part of Codingal is built to support strong learning
          outcomes and smooth teaching.
        </p>
      </div>

      <div className="flex flex-col gap-8">
        {SECTIONS.map((section, index) => (
          <AlternatingSection
            key={index}
            badge={section.badge}
            items={section.items}
            imageSrc={section.imageSrc}
            imageAlt={section.imageAlt}
            imageWidth={section.imageWidth}
            imageHeight={section.imageHeight}
            reverse={section.reverse}
          />
        ))}
      </div>
    </div>
  </div>
)

export default LearningCommunity
